import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import CountProductListTable from "./CountProductListTable";
import SpecBackButton from "../../components/SpecBackButton";
import SpecSwitch from "../../components/SpecSwitch";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import inventoryClient from "../../clients/inventoryClient";
import SpecSpinner from "../../components/SpecSpinner";
import useVendorCurrency from "../../hooks/useVendorCurrency";
import Store, { activeRouteKeys } from "../../Store/Store";
import utilFunctions from "../../utilities/utilFunctions";
import inventoryObjects, {
  inventoryCountTypesMap,
} from "../../clients/inventoryObjects";
import CreateMissingCostsModal from "../../components/Modals/CreateMissingCostsModal";
import MissingParLevelsModal from "../../components/Modals/MissingParLevelsModal";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import OrderToParConfirmationModal from "../../components/Modals/OrderToParConfirmationModal";
import OrderToParSuccessModal from "../../components/Modals/OrderToParSuccessModal";
import OrderToParNoValidOrdersModal from "../../components/Modals/OrderToParNoValidOrdersModal";
import EditStockCountRecordQuantityModal from "../../components/Modals/EditStockCountRecordQuantityModal";
import AddProductToCompletedStockCountModal from "../../components/Modals/AddProductToCompletedStockCountModal";
import SnackBarSuccess from "../../components/SnackBarSuccess";
import SnackBarError from "../../components/SnackBarError";

// reportType enum values on the backend:
// 0 = BasicStockCountReport, 1 = ComparisonByDate, 2 = CountReport
const COUNT_REPORT_TYPE = 2;
// CountReportScope on the backend: 0 = Overall, 1 = ByArea
const COUNT_REPORT_SCOPE_OVERALL = 0;
const COUNT_REPORT_SCOPE_BY_AREA = 1;

const StockCountReview = () => {
  const [costsModalOpen, toggleCostsModalOpen] = useState(false);
  const [parsModalOpen, setParsModalOpen] = useState(false);
  const [recordForCostSetting, setRecordForCostSetting] = useState(null);
  const [editQuantityModalOpen, setEditQuantityModalOpen] = useState(false);
  const [editQuantityRecords, setEditQuantityRecords] = useState([]);
  const [editQuantityProduct, setEditQuantityProduct] = useState(null);
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);
  const [addProductPrefilledAreaId, setAddProductPrefilledAreaId] =
    useState(null);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [alertSuccess, setAlertSuccess] = useState(undefined);
  const [filter, setFilter] = useState(false);
  const [showOrderConfirmationModal, toggleOrderConfirmationModal] =
    useState(false);
  const [noProductsUnderPar, toggleNoUnderParProducts] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [showSuccessOrderToParModal, toggleSuccessOrderToParModal] =
    useState(false);
  const [orderToParError, setOrderToParError] = useState(false);
  const [showNoValidOrdersModal, toggleNoValidOrdersModal] = useState(false);
  const [groupByArea, setGroupByArea] = useState(false);
  const [reportMenuAnchor, setReportMenuAnchor] = useState(null);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const areaRefs = useRef({});
  const [searchParams] = useSearchParams();
  const store = useContext(Store);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { establishmentid: establishmentId, stockcountid: stockCountId } =
    useParams();
  const { currencySymbol, currency, locale } = useVendorCurrency(
    store.user?.country
  );
  const { isLoading, data } = useQuery(
    ["stock-count", { establishmentId, stockCountId }],
    inventoryClient.getStockCountById
  );

  const handleCostsModalOpen = (record) => {
    setRecordForCostSetting(record);
    toggleCostsModalOpen(true);
  };

  const handleClostCostModal = () => {
    setRecordForCostSetting(null);
    toggleCostsModalOpen(false);
  };

  const handleParsModalOpen = () => {
    setParsModalOpen(!parsModalOpen);
  };

  const buildEditRecords = (clickedRecord) => {
    const productInfo = {
      productName: clickedRecord.historicalProductName,
      formatName: clickedRecord.historicalSizingDisplay,
    };
    if (groupByArea) {
      // The clicked row IS the per-area record. Single-record edit.
      const owningArea = (stockCount?.areaCounts || []).find((a) =>
        (a.stockCountRecords || []).some((r) => r.id === clickedRecord.id)
      );
      return {
        records: [
          {
            id: clickedRecord.id,
            quantityFound: clickedRecord.quantityFound,
            areaName: owningArea?.name || clickedRecord.area || "",
          },
        ],
        product: productInfo,
      };
    }
    // Ungrouped: clicked row is the parent rollup. Gather every per-area record
    // for this productFormatId so the user can edit them all in one shot.
    const records = [];
    for (const areaCount of stockCount?.areaCounts || []) {
      for (const r of areaCount.stockCountRecords || []) {
        if (r.productFormatId === clickedRecord.productFormatId) {
          records.push({
            id: r.id,
            quantityFound: r.quantityFound,
            areaName: areaCount.name,
          });
        }
      }
    }
    return { records, product: productInfo };
  };

  const handleEditQuantityModalOpen = (record) => {
    const { records, product } = buildEditRecords(record);
    setEditQuantityRecords(records);
    setEditQuantityProduct(product);
    setEditQuantityModalOpen(true);
  };

  const handleCloseEditQuantityModal = () => {
    setEditQuantityRecords([]);
    setEditQuantityProduct(null);
    setEditQuantityModalOpen(false);
  };

  const handleAddProductModalOpen = () => {
    setAddProductPrefilledAreaId(null);
    setAddProductModalOpen(true);
  };

  const handleAddProductModalOpenForArea = (areaCountId) => {
    setAddProductPrefilledAreaId(areaCountId);
    setAddProductModalOpen(true);
  };

  const handleCloseAddProductModal = () => {
    setAddProductModalOpen(false);
    setAddProductPrefilledAreaId(null);
  };

  const handleCloseSortMenu = () => {
    setSortMenuAnchor(null);
  };

  const handleOpenSortMenu = (e) => {
    setSortMenuAnchor(e.currentTarget);
  };

  const setUnderFilter = () => {
    if (filter === "underPar") {
      setFilter(false);
      handleCloseSortMenu();
      return;
    }
    setFilter("underPar");
    handleCloseSortMenu();
  };

  const setNoParFilter = () => {
    if (filter === "noPar") {
      setFilter(false);
      handleCloseSortMenu();
      return;
    }
    setFilter("noPar");
    handleCloseSortMenu();
  };

  const handleCloseOrderToParModal = () => {
    toggleOrderConfirmationModal(false);
    toggleNoUnderParProducts(false);
  };

  const handleOrderToPar = (confirmationGiven) => {
    handleCloseOrderToParModal();
    const hasUnderPar = stockCount?.stockCountRecords.some(
      (record) =>
        record.finalParStatus === inventoryObjects.parStatusTypes.under
    );

    if (!hasUnderPar) {
      toggleNoUnderParProducts(true);
      return;
    }

    orderToParMutation.mutate({
      establishmentId,
      countId: stockCountId,
      confirmationGiven: confirmationGiven === true ? true : false,
    });
  };

  useEffect(() => {
    store.updateActiveRoute(
      activeRouteKeys.stockCount,
      `spec/${establishmentId}/count-review/${stockCountId}`
    );
  }, []);

  const orderToParMutation = useMutation(inventoryClient.orderToPar, {
    onSuccess: (res) => {
      const resData = res.data;
      setOrderToParError(false);
      if (resData.needsConfirmation) {
        setConfirmationData(resData);
        toggleOrderConfirmationModal(true);
        return;
      } else if (resData.noOrdersToCreate) {
        setConfirmationData(resData);
        toggleNoValidOrdersModal(true);
        return;
      }

      toggleSuccessOrderToParModal(true);
    },
    onError: (error) => {
      console.error("Error ordering to par:", error);
      setOrderToParError(true);
    },
  });

  const generateCountReportMutation = useMutation(
    inventoryClient.generateStockCountReport,
    {
      onSuccess: (response) => {
        if (response?.status === 204) {
          setErrorMessage("There was no data available to generate a report.");
          return;
        }
        const url = response?.data?.report?.stockCountReportMetadata?.s3PresignedUrl;
        if (!url) {
          setErrorMessage("Report generation failed!");
          return;
        }
        utilFunctions.downloadFile(url);
        setAlertSuccess(
          "Report generated successfully, your download should have started."
        );
      },
      onError: () => {
        setErrorMessage("Report generation failed!");
      },
    }
  );

  const handleOpenReportMenu = (e) => {
    setReportMenuAnchor(e.currentTarget);
  };

  const handleCloseReportMenu = () => {
    setReportMenuAnchor(null);
  };

  const handleCreateReport = (scope) => {
    handleCloseReportMenu();
    generateCountReportMutation.mutate({
      establishmentId,
      stockCountId,
      reportType: COUNT_REPORT_TYPE,
      // Reports generated from the review screen default to including the
      // default PU per unit cost column.
      countReportFilters: { scope, includeDefaultPuPerUnitCost: true },
    });
  };

  const handleAreaPillClick = (areaCountId) => {
    const node = areaRefs.current[areaCountId];
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const showCongrats = searchParams.get("congrats") != null;

  if (isLoading) {
    return <SpecSpinner open text={"Loading Review..."} />;
  }

  const stockCount = data.stockCount;

  const applyFilter = (records) => {
    if (filter === false) return records;
    const targetStatus =
      filter === "noPar"
        ? inventoryObjects.parStatusTypes.unknown
        : inventoryObjects.parStatusTypes.under;
    return (records || []).filter((x) => x.finalParStatus === targetStatus);
  };

  const flatRecords = applyFilter(stockCount?.stockCountRecords);
  const areaCounts = stockCount?.areaCounts || [];

  return (
    <>
      <Grid
        container
        direction="column"
        sx={{
          maxWidth: "2440px",
          padding: 4,
          marginTop: 6,
        }}
      >
        <Box>
          <SpecBackButton
            backText="Stock Count"
            onClick={() => navigate(`/spec/${establishmentId}/stockcount`)}
          />
        </Box>
        <Box sx={{ marginTop: 6 }}>
          <Typography variant="subtitle1">
            {inventoryCountTypesMap[stockCount.type]}
          </Typography>
        </Box>
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="h2">{stockCount.name}</Typography>
        </Box>
        {orderToParError ? (
          <Alert severity="error">
            There was an error trying to order to par. You can try again. If the
            problem persists please contact support.
          </Alert>
        ) : null}
        {showCongrats ? (
          <Box>
            <Typography variant="h2" sx={{ marginTop: 6 }}>
              Congratulations!
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ marginTop: 6, fontWeight: 700 }}
            >
              You have successfully submitted the count {stockCount?.name}
            </Typography>
          </Box>
        ) : null}
        <Box
          sx={{
            width: "100%",
            height: "68px",
            display: "flex",
            alignItems: "center",
            marginTop: 6,
            backgroundColor: (theme) => theme.palette.pure.white,
            border: "1px solid",
            borderColor: (theme) => theme.palette.terrain[300],
            borderRadius: 2,
          }}
        >
          <Grid
            container
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: 2,
            }}
          >
            <Grid item sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Total Value on Hand:
              </Typography>
            </Grid>
            <Grid item sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle2">
                {currencySymbol}
                {utilFunctions.convertCentsToLargerFormatCurrency(
                  stockCount.finalTotalValue?.amount || 0,
                  locale,
                  currency
                )}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ marginTop: 6 }}>
          <Grid
            container
            direction="row"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              rowGap: 2,
            }}
          >
            <Grid item sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                sx={{ marginLeft: 0 }}
                control={
                  <SpecSwitch
                    checked={groupByArea}
                    onChange={() => setGroupByArea(!groupByArea)}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Group by Area
                  </Typography>
                }
              />
            </Grid>
            <Grid
              item
              sx={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", rowGap: 1 }}
            >
              <Button
                variant="outlined"
                sx={{
                  marginRight: 2,
                  border: "2px solid",
                  borderColor: (theme) => theme.palette.primary.main,
                }}
                onClick={handleOpenReportMenu}
                disabled={generateCountReportMutation.isLoading}
              >
                {generateCountReportMutation.isLoading ? (
                  <CircularProgress size={24} color="primary" />
                ) : (
                  <>
                    <Typography variant="largeButton">Create Report</Typography>
                    <ArrowDropDownIcon sx={{ fontSize: 20, marginLeft: 1 }} />
                  </>
                )}
              </Button>
              <Menu
                anchorEl={reportMenuAnchor}
                open={!!reportMenuAnchor}
                onClose={handleCloseReportMenu}
              >
                <MenuItem
                  sx={{ margin: "5px" }}
                  id="report-full-count"
                  onClick={() => handleCreateReport(COUNT_REPORT_SCOPE_OVERALL)}
                >
                  Full Count
                </MenuItem>
                <MenuItem
                  sx={{ margin: "5px" }}
                  id="report-by-area"
                  onClick={() => handleCreateReport(COUNT_REPORT_SCOPE_BY_AREA)}
                >
                  Records by Area
                </MenuItem>
              </Menu>
              <Button
                variant="outlined"
                sx={{
                  marginRight: 2,
                  border: "2px solid",
                  borderColor: (theme) => theme.palette.primary.main,
                }}
                onClick={handleOpenSortMenu}
              >
                <TuneOutlinedIcon sx={{ fontSize: 20, marginRight: 1 }} />
                <Typography variant="largeButton">Filter</Typography>
              </Button>
              <Menu
                anchorEl={sortMenuAnchor}
                open={!!sortMenuAnchor}
                onClose={handleCloseSortMenu}
              >
                <MenuItem
                  sx={{ margin: "5px" }}
                  selected={filter === "underPar"}
                  id="filter-under"
                  onClick={setUnderFilter}
                >
                  Under Par
                </MenuItem>
                <MenuItem
                  sx={{ margin: "5px" }}
                  selected={filter === "noPar"}
                  id="filter-no-par"
                  onClick={setNoParFilter}
                >
                  No Par Set
                </MenuItem>
              </Menu>
              <Button
                variant="outlined"
                sx={{
                  marginRight: 2,
                  border: "2px solid",
                  borderColor: (theme) => theme.palette.primary.main,
                }}
                onClick={handleOrderToPar}
                disabled={orderToParMutation.isLoading}
              >
                {orderToParMutation?.isLoading ? (
                  <CircularProgress size={24} color="primary" />
                ) : (
                  <Typography variant="largeButton">Order to Par</Typography>
                )}
              </Button>
              <Button
                variant="contained"
                onClick={handleAddProductModalOpen}
              >
                <Typography variant="largeButton">Add Product</Typography>
              </Button>
            </Grid>
          </Grid>
        </Box>
        {groupByArea ? (
          <>
            {areaCounts.length > 0 ? (
              <Box
                sx={{
                  marginTop: 6,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {areaCounts.map((areaCount) => (
                  <Chip
                    key={areaCount.id}
                    label={
                      <Typography variant="body2">{areaCount.name}</Typography>
                    }
                    size="medium"
                    clickable
                    variant="outlined"
                    onClick={() => handleAreaPillClick(areaCount.id)}
                    sx={{
                      backgroundColor: (theme) => theme.palette.pure.white,
                      borderColor: (theme) => theme.palette.primary[800],
                    }}
                  />
                ))}
              </Box>
            ) : null}
            {areaCounts.map((areaCount) => (
              <Box
                key={areaCount.id}
                sx={{ marginTop: 6 }}
                ref={(node) => {
                  if (node) {
                    areaRefs.current[areaCount.id] = node;
                  }
                }}
              >
                <Grid
                  container
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <Grid item>
                    <Typography variant="h2">{areaCount.name}</Typography>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        handleAddProductModalOpenForArea(areaCount.id)
                      }
                      sx={{
                        border: "2px solid",
                        borderColor: (theme) => theme.palette.primary.main,
                      }}
                    >
                      <Typography variant="largeButton">
                        Add Product to Area
                      </Typography>
                    </Button>
                  </Grid>
                </Grid>
                <CountProductListTable
                  records={areaCount.stockCountRecords || []}
                  openRecordCostSetModal={handleCostsModalOpen}
                  toggleParsModalOpen={handleParsModalOpen}
                  openEditQuantityModal={handleEditQuantityModalOpen}
                  hidePar
                />
              </Box>
            ))}
          </>
        ) : (
          <Box sx={{ marginTop: 6 }}>
            <CountProductListTable
              records={flatRecords}
              openRecordCostSetModal={handleCostsModalOpen}
              toggleParsModalOpen={handleParsModalOpen}
              openEditQuantityModal={handleEditQuantityModalOpen}
            />
          </Box>
        )}
        <CreateMissingCostsModal
          headerText="Missing Costs"
          buttonText="Save Details"
          isOpen={costsModalOpen}
          record={recordForCostSetting}
          closeModal={handleClostCostModal}
        />
        <MissingParLevelsModal
          headerText="Missing Par Levels"
          buttonText="Save Pars"
          isOpen={parsModalOpen}
          toggleModalOpen={handleParsModalOpen}
        />
        <OrderToParConfirmationModal
          isOpen={showOrderConfirmationModal || noProductsUnderPar}
          showNoUnderPar={noProductsUnderPar}
          closeModal={handleCloseOrderToParModal}
          loading={orderToParMutation.isLoading}
          orderToParData={confirmationData}
          orderToParMutation={orderToParMutation}
          orderCallback={handleOrderToPar}
        />
        <OrderToParSuccessModal
          isOpen={showSuccessOrderToParModal}
          closeModal={() => {
            toggleSuccessOrderToParModal(false);
          }}
          establishmentId={establishmentId}
        />
        <OrderToParNoValidOrdersModal
          isOpen={showNoValidOrdersModal}
          closeModal={() => {
            toggleNoValidOrdersModal(false);
          }}
          orderToParData={confirmationData}
        />
        <EditStockCountRecordQuantityModal
          isOpen={editQuantityModalOpen}
          handleCloseEditQuantityModal={handleCloseEditQuantityModal}
          records={editQuantityRecords}
          product={editQuantityProduct}
          stockCountId={stockCountId}
          establishmentId={establishmentId}
          onSuccessCallback={() => {
            setAlertSuccess(
              editQuantityRecords.length > 1
                ? "Quantities updated successfully!"
                : "Quantity updated successfully!"
            );
          }}
        />
        <AddProductToCompletedStockCountModal
          isOpen={addProductModalOpen}
          handleCloseAddProductModal={handleCloseAddProductModal}
          stockCountId={stockCountId}
          establishmentId={establishmentId}
          areaCounts={areaCounts}
          prefilledAreaCountId={addProductPrefilledAreaId}
          onSuccessCallback={() => {
            queryClient.invalidateQueries(["stock-count", { establishmentId, stockCountId }]);
            queryClient.refetchQueries(["stock-count", { establishmentId, stockCountId }]);
            setAlertSuccess(
              addProductPrefilledAreaId
                ? "Records added to area successfully!"
                : "Records added successfully!"
            );
          }}
        />
      </Grid>
      <SnackBarSuccess
        alertSuccess={alertSuccess}
        setAlertSuccess={setAlertSuccess}
        durationLength={3000}
      />
      <SnackBarError
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
    </>
  );
};

export default StockCountReview;
